import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { PAGE_SIZE } from '../constants/pagination'

const readPositiveInteger = (value: string | null, fallback: number) => {
    const parsed = Number(value)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

export function useListPagination<T>(items: T[]) {
    const [searchParams, setSearchParams] = useSearchParams()
    const requestedPage = readPositiveInteger(searchParams.get('page'), 1)
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
    const page = Math.min(requestedPage, totalPages)

    const updateParams = useCallback((nextPage: number) => {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set('page', String(nextPage))
        nextParams.delete('limit')
        setSearchParams(nextParams, { replace: true })
    }, [searchParams, setSearchParams])

    useEffect(() => {
        if (requestedPage !== page || searchParams.has('limit')) {
            updateParams(page)
        }
    }, [page, requestedPage, searchParams, updateParams])

    const paginatedItems = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE
        return items.slice(start, start + PAGE_SIZE)
    }, [items, page])

    return {
        page,
        totalItems: items.length,
        paginatedItems,
        setPage: (nextPage: number) => updateParams(Math.max(1, Math.min(nextPage, totalPages))),
    }
}
